import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Modal } from 'antd';
import { format } from 'date-fns';
import config from '../../../config';
import { buildCommandeColumns, renderCommandeCard, formatCommandeCode } from './commandeShared';
import { NdoeResponsiveTable, NdoeRecherche, NdoeError } from '../../../components/ndoe';
import { messageErreur } from '../../../components/ndoe/chargement';
import MouvClientDetail from '../../mouvement/mouvementClientDetail/MouvClientDetail';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

/* ---------------------------------------------------------------------------
   Recherche avancée des commandes (critères validés 16/07) : plage de dates
   LIBRE (serveur, /commandePlage) puis filtres INSTANTANÉS sur la liste
   chargée — recherche globale (code, cliente, téléphone), livreur, état de
   livraison, shop. Les options livreur/état/shop se construisent depuis les
   données reçues : zéro appel supplémentaire.
--------------------------------------------------------------------------- */

const jour = (d) => format(d, 'yyyy-MM-dd');

const RechercheAvancee = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user?.currentUser);
  const isAdmin = user?.role === 'admin';

  const [debut, setDebut] = useState(jour(new Date(Date.now() - 30 * 86400000)));
  const [fin, setFin] = useState(jour(new Date()));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [charge, setCharge] = useState(false);
  const [erreur, setErreur] = useState(null);

  // Filtres instantanés (client)
  const [q, setQ] = useState('');
  const [livreurSel, setLivreurSel] = useState(null);
  const [etatSel, setEtatSel] = useState(null);
  const [shopSel, setShopSel] = useState(null);

  const [open, setOpen] = useState(false);
  const [idClient, setIdClient] = useState({});

  const chercher = async () => {
    setLoading(true);
    setErreur(null);
    try {
      const { data: rows } = await axios.get(`${DOMAIN}/api/commande/commandePlage`, { params: { debut, fin } });
      setData(Array.isArray(rows) ? rows : []);
      setCharge(true);
    } catch (e) {
      // UNE PANNE N'EST PAS « AUCUNE COMMANDE » (10/08/2026). `setCharge(true)`
      // faisait afficher « Aucune commande ne correspond à ces critères » : on
      // pouvait conclure qu'il n'y avait rien vendu sur la période, alors que la
      // requête n'était jamais arrivée.
      setData([]); setCharge(true);
      setErreur(messageErreur(e, 'les commandes de cette période'));
    } finally { setLoading(false); }
  };

  useEffect(() => { chercher(); /* premier chargement : 30 derniers jours */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Options construites depuis les données (dédoublonnées).
  const livreurOptions = useMemo(() => {
    const vus = new Map();
    data.forEach((r) => (r.livreurs || '').split(', ').filter(Boolean).forEach((nom) => vus.set(nom, true)));
    return [...vus.keys()].sort().map((nom) => ({ value: nom, label: nom }));
  }, [data]);
  const etatOptions = useMemo(() => {
    const vus = new Set(data.map((r) => r.livraison_statut?.label).filter(Boolean));
    return [...vus].sort().map((l) => ({ value: l, label: l }));
  }, [data]);
  const shopOptions = useMemo(() => {
    const vus = new Set(data.map((r) => r.id_shop).filter((v) => v != null));
    return [...vus].sort().map((s) => ({ value: s, label: s === 1 ? 'Shop Ngaliema' : `Shop ${s}` }));
  }, [data]);

  // RECHERCHE LIBRE « QUASI TOUS CRITÈRES » (demande proprio 16/07).
  // Le champ balaie TOUT ce qui est affiché sur la ligne : code, cliente,
  // téléphone, livreur, état de livraison, shop, statut. On tape ce qu'on a en
  // tête sans se demander « dans quel filtre ça va ». Les menus déroulants ne
  // servent plus qu'à AFFINER.
  //
  // Chaque mot doit correspondre (ET) : « elie livree » trouve les commandes
  // livrées par Élie. C'est ce qui rend le champ combinable, donc utile.
  const texteDe = (r) => [
    formatCommandeCode(r),
    String(r.id_commande),
    r.nom,
    r.telephone,
    r.livreurs,
    r.livraison_statut?.label,
    r.nom_statut,
    r.id_shop === 1 ? 'Shop Ngaliema' : (r.id_shop != null ? `Shop ${r.id_shop}` : ''),
  ].filter(Boolean).join(' ').toLowerCase();

  const filtres = useMemo(() => data.filter((r) => {
    if (q) {
      const foin = texteDe(r);
      // Le téléphone est comparé chiffres-à-chiffres : on le cherche « 0812… »
      // alors qu'il est stocké « +243 812… ».
      const foinTel = String(r.telephone || '').replace(/[^0-9]/g, '');
      const mots = q.toLowerCase().split(/\s+/).filter(Boolean);
      const ok = mots.every((m) => {
        if (foin.includes(m)) return true;
        const chiffres = m.replace(/[^0-9]/g, '');
        return chiffres.length >= 3 && foinTel.includes(chiffres);
      });
      if (!ok) return false;
    }
    if (livreurSel && !(r.livreurs || '').split(', ').includes(livreurSel)) return false;
    if (etatSel && r.livraison_statut?.label !== etatSel) return false;
    if (shopSel != null && r.id_shop !== shopSel) return false;
    return true;
  }), [data, q, livreurSel, etatSel, shopSel]);

  const showClient = (id) => { setIdClient(id); setOpen(true); };
  const columns = buildCommandeColumns({
    onClient: showClient,
    onEdit: (id) => navigate(`/Editcommande/${id}`),
    isAdmin,
    indexOffset: 0,
  });

  const nbFiltresActifs = [q, livreurSel, etatSel, shopSel].filter((v) => v != null && v !== '').length;

  return (
    <>
      <NdoeRecherche
        q={q}
        onQ={setQ}
        // Le placeholder ÉNUMÈRE ce que le champ couvre : sans ça, personne ne
        // devine qu'on peut y taper un nom de livreur ou un état.
        placeholder="Rechercher : code, cliente, téléphone, livreur, état, shop… (plusieurs mots possibles)"
        periode={{ debut, fin }}
        onPeriode={(d, f) => { setDebut(d); setFin(f); }}
        onChercher={chercher}
        chargement={loading}
        facettes={[
          { cle: 'livreur', placeholder: 'Livreur', options: livreurOptions, valeur: livreurSel, onChange: setLivreurSel, largeur: 150 },
          { cle: 'etat', placeholder: 'État de livraison', options: etatOptions, valeur: etatSel, onChange: setEtatSel, largeur: 170 },
          { cle: 'shop', placeholder: 'Shop', options: shopOptions, valeur: shopSel, onChange: setShopSel, largeur: 140 },
        ]}
        actifs={nbFiltresActifs}
        onEffacer={() => { setQ(''); setLivreurSel(null); setEtatSel(null); setShopSel(null); }}
        // « 0 commande » est un CHIFFRE : on ne l'annonce pas quand on n'a rien
        // reçu. Le résumé se tait, la raison s'affiche juste en dessous.
        resume={charge && !erreur && (
          <>
            <b>{filtres.length}</b>
            {filtres.length !== data.length && <> sur {data.length}</>}
            &nbsp;commande{filtres.length > 1 ? 's' : ''}
            <span>· du {debut} au {fin}</span>
          </>
        )}
      />

      {/* La panne se dit AVANT le tableau, pas à sa place : sinon « Aucune
          commande » se lit comme un constat de vente. */}
      {erreur && (
        <NdoeError
          title="La recherche n’a pas abouti"
          text={erreur}
          onRetry={() => chercher()}
        />
      )}

      <NdoeResponsiveTable
        data={filtres}
        columns={columns}
        rowKey="id_commande"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 15 }}
        renderCard={(record) => renderCommandeCard(record, {
          onOpen: (id) => navigate(`/listeDetailView/${id}`),
          onProducts: (id) => navigate(`/commandes/${id}`),
          onEdit: (id) => navigate(`/Editcommande/${id}`),
          isAdmin,
        })}
        // Le bandeau juste au-dessus porte déjà la phrase entière et le bouton
        // « Réessayer » : ici on redit seulement pourquoi le tableau est vide,
        // sans répéter tout le message (illisible sur un écran de téléphone).
        empty={erreur
          ? { icon: '⚠️', title: 'Liste non chargée', text: 'Voir le message ci-dessus.' }
          : { icon: '🔍', title: 'Aucune commande', text: 'Aucune commande ne correspond à ces critères.' }}
      />

      <Modal title="Information de client" centered open={open} onCancel={() => setOpen(false)} width={850} footer={[]}>
        <MouvClientDetail idClients={idClient} />
      </Modal>
    </>
  );
};

export default RechercheAvancee;
